/**
 * <s-fbt> — "Pair it with our best sellers" bundle on the product page.
 * Keeps the total in sync with ticked items and chosen sizes, follows the main size picker,
 * and adds every ticked item to the cart in one request, then refreshes and opens the cart drawer.
 *
 * Commands:  --add-selected  (from the button via commandfor/command)
 */
const formatINR = (paise) =>
  '₹' + (paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

class FrequentlyBought extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target.matches('[data-fbt-check], [data-fbt-variant]')) this.#update();
    });
    this.addEventListener('command', (event) => {
      if (event.command === '--add-selected') this.#addSelected();
    });
    // Follow the main product size picker so "This item" matches what the shopper chose above.
    document.addEventListener('change', () => queueMicrotask(() => this.#syncMain()));
    this.#update();
  }

  get #items() {
    return [...this.querySelectorAll('[data-fbt-item]')];
  }

  #variantOf(item) {
    const field = item.querySelector('[data-fbt-variant]');
    if (!field) return null;
    const option = field instanceof HTMLSelectElement ? field.selectedOptions[0] : field;
    const price = Number(option?.dataset.price || 0);
    const compare = Math.max(price, Number(option?.dataset.compare || 0));
    return { id: Number(field.value), price, compare };
  }

  #syncMain() {
    const mainField = document.querySelector('form[action*="/cart/add"] [name="id"]');
    const select = this.querySelector('[data-fbt-main] select[data-fbt-variant]');
    if (!mainField || !(select instanceof HTMLSelectElement) || !mainField.value) return;
    if (select.value === mainField.value) return;
    const match = [...select.options].find((option) => option.value === mainField.value);
    if (!match) return;
    select.value = mainField.value;
    this.#update();
  }

  #update() {
    let total = 0;
    let compareTotal = 0;
    let count = 0;
    for (const item of this.#items) {
      const checked = item.querySelector('[data-fbt-check]')?.checked;
      const variant = this.#variantOf(item);
      item.classList.toggle('is-off', !checked);
      const priceEl = item.querySelector('[data-fbt-price]');
      if (priceEl && variant) priceEl.textContent = formatINR(variant.price);
      const wasEl = item.querySelector('[data-fbt-was]');
      if (wasEl && variant) {
        wasEl.textContent = formatINR(variant.compare);
        wasEl.closest('s').hidden = variant.compare <= variant.price;
      }
      if (checked && variant) {
        total += variant.price;
        compareTotal += variant.compare;
        count += 1;
      }
    }
    const totalEl = this.querySelector('[data-fbt-total]');
    if (totalEl) totalEl.textContent = formatINR(total);
    // Savings against the products' regular (compare-at) prices.
    const saving = compareTotal - total;
    this.querySelectorAll('[data-fbt-save-wrap]').forEach((el) => { el.hidden = saving <= 0; });
    const wasTotalEl = this.querySelector('[data-fbt-was-total]');
    if (wasTotalEl) wasTotalEl.textContent = formatINR(compareTotal);
    const saveEl = this.querySelector('[data-fbt-save]');
    if (saveEl) saveEl.textContent = formatINR(saving);
    const pctEl = this.querySelector('[data-fbt-save-pct]');
    if (pctEl && compareTotal > 0) pctEl.textContent = String(Math.round((saving / compareTotal) * 100));
    const button = this.querySelector('[data-fbt-add]');
    if (button) button.disabled = count === 0;
    // The automatic 3+ attars discount note only makes sense while 3 or more are ticked.
    const multi = this.querySelector('[data-fbt-multi]');
    if (multi) multi.hidden = count < 3;
  }

  async #addSelected() {
    const button = this.querySelector('[data-fbt-add]');
    const label = this.querySelector('[data-fbt-label]');
    const message = this.querySelector('[data-fbt-msg]');
    const items = this.#items
      .filter((item) => item.querySelector('[data-fbt-check]')?.checked)
      .map((item) => this.#variantOf(item))
      .filter(Boolean)
      .map((variant) => ({ id: variant.id, quantity: 1 }));
    if (!items.length || !button) return;

    const originalLabel = label?.textContent;
    button.disabled = true;
    if (label) label.textContent = this.dataset.msgAdding;
    if (message) message.textContent = '';

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || data.message || this.dataset.msgError);
      }
      if (message) message.textContent = this.dataset.msgAdded;
      await customElements.whenDefined('s-cart').catch(() => {});
      await customElements.get('s-cart')?.refreshMountedCarts?.();
      const drawer = document.getElementById('cart-drawer');
      if (typeof window.qcShowAddedBar === 'function') {
        await window.qcShowAddedBar();
      } else if (drawer instanceof HTMLDialogElement && !drawer.open) {
        drawer.showModal();
        drawer.dispatchEvent(new CustomEvent('s-cart:opened', { bubbles: true, detail: { dialogId: drawer.id } }));
      }
    } catch (error) {
      if (message) message.textContent = error?.message || this.dataset.msgError;
    } finally {
      const succeeded = message?.textContent === this.dataset.msgAdded;
      const reset = () => {
        button.classList.remove('is-done');
        if (label) label.textContent = originalLabel;
        this.#update();
      };
      if (succeeded) {
        // Brief green "Added" state on the button before it returns to normal.
        button.classList.add('is-done');
        if (label) label.textContent = this.dataset.msgAdded;
        setTimeout(reset, 2200);
      } else {
        reset();
      }
    }
  }
}

if (!customElements.get('s-fbt')) customElements.define('s-fbt', FrequentlyBought);
